import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../api/initSupabase';
import { localAnalytics } from '../utils/analytics';
import { showToast } from '../utils/toast';
import { logErrorsWithMessage } from '../utils/errors';
import {
  FontText,
  PrimaryButton,
  SecondaryButton,
  CloseButton,
} from './common';

interface BookInProgressModalProps {
  bookId: number;
  bookTitle: string;
  bookSlug: string;
  currentChapter: number;
  totalChapters: number;
  isOpen: boolean;
  onClose: () => void;
}

export const BookInProgressModal: React.FC<BookInProgressModalProps> = ({
  bookId,
  bookTitle,
  bookSlug,
  currentChapter,
  totalChapters,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const { theme, themeMode } = useTheme();
  const { userId, isAuthenticated } = useAuth();

  const [timeRemaining, setTimeRemaining] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const updateCountdown = () => {
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + 7);
      targetDate.setHours(0, 0, 0, 0);

      const now = new Date();
      const difference = targetDate.getTime() - now.getTime();

      if (difference <= 0) {
        setTimeRemaining('0d:0h:0m');
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

      setTimeRemaining(`${days}d:${hours}h:${minutes}m`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);

    return () => clearInterval(interval);
  }, [isOpen]);

  const handleClose = () => {
    localAnalytics().logEvent('BookInProgressModalClosed', {
      screen: 'BookInProgressModal',
      action: 'ModalClosed',
      userId,
      bookId,
      bookTitle,
    });

    onClose();
  };

  const handleDiscoverSimilar = () => {
    localAnalytics().logEvent('BookInProgressDiscoverSimilarClicked', {
      screen: 'BookInProgressModal',
      action: 'DiscoverSimilarClicked',
      userId,
      bookId,
      bookTitle,
    });

    onClose();
  };

  const handleNotifyMe = async () => {
    if (!isAuthenticated || !userId) {
      showToast.error(t('sign_in_required'));
      return;
    }

    setLoading(true);

    try {
      localAnalytics().logEvent('BookInProgressNotifyMeClicked', {
        screen: 'BookInProgressModal',
        action: 'NotifyMeClicked',
        userId,
        bookId,
        bookTitle,
      });

      const { data: existingFavorite } = await supabase
        .from('book_favorite')
        .select('*')
        .eq('user_id', userId)
        .eq('book_id', bookId)
        .maybeSingle();

      if (!existingFavorite) {
        const { error } = await supabase.from('book_favorite').insert([
          {
            user_id: userId,
            book_id: bookId,
          },
        ]);

        if (error) throw error;
      }

      showToast.success(t('notification_added_success'));

      localAnalytics().logEvent('BookInProgressNotifyMeSuccess', {
        screen: 'BookInProgressModal',
        action: 'NotifyMeSuccess',
        userId,
        bookId,
        bookTitle,
        wasAlreadyFavorited: !!existingFavorite,
      });
    } catch (error) {
      logErrorsWithMessage(error, t('failed_to_add_notification'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const remainingChapters = Math.max(0, totalChapters - currentChapter + 1);

  const isMobile = window.innerWidth <= 768;

  const styles: Record<string, React.CSSProperties> = {
    container: {
      backgroundColor: theme.colors.background,
      padding: isMobile ? 20 : 40,
      maxWidth: 500,
      margin: '0 auto',
      textAlign: 'center',
      minHeight: isMobile ? '50vh' : '60vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      marginBottom: 100,
    },
    closeButton: {
      alignSelf: 'flex-end',
      marginBottom: 20,
    },
    pencilIcon: {
      fontSize: isMobile ? 40 : 48,
      marginBottom: isMobile ? 16 : 20,
    },
    inProgressText: {
      color: theme.colors.textSecondary,
      marginBottom: 16,
      fontStyle: 'italic',
    },
    titleText: {
      color: theme.colors.text,
      marginBottom: 20,
      lineHeight: 1.4,
    },
    countdownContainer: {
      backgroundColor: theme.colors.background,
      borderRadius: 12,
      padding: 16,
      marginBottom: 30,
    },
    countdownLabel: {
      color: theme.colors.textSecondary,
      marginBottom: 8,
    },
    countdownTime: {
      color: theme.colors.accent,
      fontSize: isMobile ? 20 : 24,
      fontWeight: 'bold',
    },
    buttonsContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 12,
    },
  };

  return (
    <div style={styles.container}>
      <CloseButton
        onClose={handleClose}
        style={styles.closeButton}
        analyticsEvent="BookInProgressModalCloseClicked"
        themeMode={themeMode}
      />

      <div style={styles.pencilIcon}>✏️</div>

      <FontText small style={styles.inProgressText}>
        {t('in_progress')}
      </FontText>

      <FontText h3 style={styles.titleText}>
        {t('author_preparing_chapters', { count: remainingChapters })}
      </FontText>

      <div style={styles.countdownContainer}>
        <FontText normal style={styles.countdownLabel}>
          {t('book_ready_in')}
        </FontText>
        <div style={styles.countdownTime}>{timeRemaining}</div>
      </div>

      <div style={styles.buttonsContainer}>
        <Link to="/" style={{ textDecoration: 'none', width: '100%' }}>
          <SecondaryButton
            onClick={handleDiscoverSimilar}
            style={{ width: '100%' }}
          >
            {t('discover_similar_books')}
          </SecondaryButton>
        </Link>

        <PrimaryButton onClick={handleNotifyMe} disabled={loading}>
          {loading ? t('adding_notification') : t('notify_me')}
        </PrimaryButton>
      </div>
    </div>
  );
};
